// src/pages/india/InspectionSetupPage.jsx
import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useLocation, useParams } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { api } from "../../api/client";
import { apiFetch } from "../../utils/apiFetch";

// ── CompanyPicker ─────────────────────────────────────────────────────────────
function CompanyPicker({ label, value, onChange, companies, onAddNew, hint, disabled }) {
  const selected = companies.find(c => c.id === value);
  return (
    <div className="space-y-3">
      {hint && <p className="text-xs text-gray-500">{hint}</p>}
      <div className={`border rounded-md p-3 min-h-[48px] ${selected ? "border-green-400 bg-green-50" : "border-dashed border-gray-300"}`}>
        <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Selected company</div>
        {!selected ? (
          <div className="text-sm text-gray-400">No company selected yet.</div>
        ) : (
          <div className="flex flex-wrap gap-2">
            <span className="inline-flex items-center gap-1 bg-white border border-gray-200 text-gray-800 text-sm px-3 py-1 rounded-full shadow-sm">
              {selected.name}
              {!disabled && (
                <button type="button" onClick={() => onChange(null)}
                  className="ml-1 text-gray-400 hover:text-red-500 text-xs leading-none">✕</button>
              )}
            </span>
          </div>
        )}
      </div>
      <CompanyDropdownWithSelect
        companies={companies}
        disabled={disabled}
        onSelect={onChange}
      />
      {!disabled && onAddNew && (
        <button type="button" onClick={onAddNew}
          className="text-xs text-blue-600 hover:underline">
          + Add new company
        </button>
      )}
    </div>
  );
}

// ── OnBehalfDropdown ──────────────────────────────────────────────────────────
function OnBehalfDropdown({ companies, onSelect }) {
  const [dropdownValue, setDropdownValue] = useState("");
  return (
    <div className="flex flex-col sm:flex-row gap-2">
      <select
        value={dropdownValue}
        onChange={(e) => setDropdownValue(e.target.value)}
        className="flex-1 min-w-0 border border-gray-300 rounded-md shadow-sm p-2 text-sm"
      >
        <option value="">— Select company —</option>
        {companies.map((c) => (
          <option key={c.id} value={c.id}>{c.name}{c.country ? ` (${c.country})` : ""}</option>
        ))}
      </select>
      <button
        type="button"
        disabled={!dropdownValue}
        onClick={() => { if (dropdownValue) { onSelect(Number(dropdownValue)); setDropdownValue(""); } }}
        className={`px-3 py-2 text-sm rounded-md transition disabled:opacity-40 ${dropdownValue ? "bg-blue-600 text-white hover:bg-blue-700" : "border border-gray-300 hover:bg-gray-50"}`}
      >
        Select
      </button>
    </div>
  );
}

// ── CompanyDropdownWithSelect ─────────────────────────────────────────────────
function CompanyDropdownWithSelect({ companies, disabled, onSelect }) {
  const [dropdownValue, setDropdownValue] = useState("");
  return (
    <div className="flex flex-col sm:flex-row gap-2">
      <select
        value={dropdownValue}
        onChange={(e) => setDropdownValue(e.target.value)}
        disabled={disabled}
        className="flex-1 min-w-0 border border-gray-300 rounded-md shadow-sm p-2 text-sm"
      >
        <option value="">— Select company —</option>
        {companies.map((c) => (
          <option key={c.id} value={c.id}>{c.name}{c.country ? ` (${c.country})` : ""}</option>
        ))}
      </select>
      {!disabled && (
        <button
          type="button"
          disabled={!dropdownValue}
          onClick={() => { if (dropdownValue) { onSelect(Number(dropdownValue)); setDropdownValue(""); } }}
          className={`px-3 py-2 text-sm rounded-md transition disabled:opacity-40 ${dropdownValue ? "bg-blue-600 text-white hover:bg-blue-700" : "border border-gray-300 hover:bg-gray-50"}`}
        >
          Select
        </button>
      )}
    </div>
  );
}

// ── CompanySearch ─────────────────────────────────────────────────────────────
function CompanySearch({ companies, value, onSelect }) {
  const [query, setQuery] = useState("");
  const selected = companies.find(c => c.id === value);
  const filtered = query.length >= 1
    ? companies.filter(c => c.name.toLowerCase().includes(query.toLowerCase()))
    : [];
  return (
    <div className="space-y-1">
      {selected ? (
        <div className="flex items-center gap-2 border border-green-400 bg-green-50 rounded-md px-3 py-2">
          <span className="text-sm text-gray-800 font-medium flex-1">{selected.name}</span>
          <button type="button" onClick={() => { onSelect(null); setQuery(""); }}
            className="text-xs text-gray-400 hover:text-red-500">✕ change</button>
        </div>
      ) : (
        <>
          <input type="text" value={query} onChange={(e) => setQuery(e.target.value)}
            placeholder="Search company by name…"
            className="w-full border border-gray-300 rounded-md p-2 text-sm bg-white" />
          {filtered.length > 0 && (
            <div className="border border-gray-200 rounded-md bg-white shadow-sm max-h-40 overflow-y-auto">
              {filtered.map(c => (
                <button key={c.id} type="button"
                  onClick={() => { onSelect(c.id); setQuery(""); }}
                  className="w-full text-left px-3 py-2 hover:bg-amber-50 text-sm">
                  {c.name}{c.country ? ` (${c.country})` : ""}
                </button>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}

// ── InspectorCompanySearch ────────────────────────────────────────────────────
function InspectorCompanySearch({ members, alreadyAdded, onSelect }) {
  const addedIds = new Set((alreadyAdded || []).map(i => i.user_id ?? i.id));
  const available = members.filter(m => !addedIds.has(m.id));
  return (
    <select
      defaultValue=""
      onChange={(e) => {
        const member = members.find(m => String(m.id) === e.target.value);
        if (member) onSelect(member);
        e.target.value = "";
      }}
      className="w-full border border-gray-300 rounded-md shadow-sm p-2 text-sm bg-white"
    >
      <option value="" disabled>Search inspectors from your company</option>
      {available.map(m => {
        const name = m.first_name || m.last_name
          ? `${m.first_name ?? ""} ${m.last_name ?? ""}`.trim()
          : m.email;
        return (
          <option key={m.id} value={String(m.id)}>{name}</option>
        );
      })}
    </select>
  );
}

// ── InspectorInviteForm ───────────────────────────────────────────────────────
function InspectorInviteForm({ inspectionId, token, activeCompany, onAdded }) {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [sending, setSending] = useState(false);
  const [err, setErr] = useState(null);
  const [done, setDone] = useState(false);
  async function handleInvite() {
    if (!name.trim() || !email.trim()) return;
    setSending(true); setErr(null);
    try {
      await api(`/india/inspections/${inspectionId}/attendees`, {
        token, method: "POST",
        body: { name: name.trim(), email: email.trim(), role_in_company: null, company_id: activeCompany?.id ?? null, send_invite: true },
      });
      setDone(true); setName(""); setEmail("");
      if (onAdded) await onAdded();
      setTimeout(() => { setDone(false); setOpen(false); }, 2000);
    } catch (e) { setErr(e?.message || "Failed to send invite"); }
    finally { setSending(false); }
  }
  return (
    <div>
      {!open ? (
        <button type="button" onClick={() => setOpen(true)}
          className="text-xs text-gray-500 hover:text-gray-700 hover:underline">
          Not in list? Invite inspector by email
        </button>
      ) : (
        <div className="mt-1 border border-gray-200 rounded-md p-3 bg-gray-50 space-y-2">
          <div className="text-xs font-semibold text-gray-600">Invite inspector</div>
          {err && <div className="text-xs text-red-600">{err}</div>}
          {done && <div className="text-xs text-green-700">✓ Invite sent!</div>}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            <input type="text" value={name} onChange={(e) => setName(e.target.value)}
              placeholder="Full name" className="w-full border border-gray-300 rounded-md p-2 text-sm" />
            <input type="email" value={email} onChange={(e) => setEmail(e.target.value)}
              placeholder="Email address" className="w-full border border-gray-300 rounded-md p-2 text-sm" />
          </div>
          <div className="flex gap-2">
            <button type="button" onClick={handleInvite}
              disabled={sending || !name.trim() || !email.trim()}
              className="px-3 py-1.5 text-xs bg-gray-900 text-white rounded hover:bg-gray-700 disabled:opacity-40">
              {sending ? "Sending…" : "Send invite"}
            </button>
            <button type="button" onClick={() => setOpen(false)}
              className="px-3 py-1.5 text-xs border rounded hover:bg-gray-50 text-gray-600">Cancel</button>
          </div>
        </div>
      )}
    </div>
  );
}

const AIS_API = "https://api.ais-harvester.app";

const REASON_OPTIONS_VESSEL = [
  "On request from client",
  "Annual survey",
  "Start on Site",
  "HSE Safety Walk",
  "Other",
];

const REASON_OPTIONS_COMPANY = [
  "On request from client",
  "Annual survey",
  "HSE maturity check",
  "Contract award",
  "HSE compliance check",
  "Other",
];

// ── VesselCard ────────────────────────────────────────────────────────────────
function VesselCard({ vessel, enrichment, enriching, onClear }) {
  const [expanded, setExpanded] = React.useState(false);

  const particulars = [
    vessel.mmsi     && { label: "MMSI",     value: String(vessel.mmsi) },
    vessel.imo      && { label: "IMO",      value: String(vessel.imo) },
    vessel.callsign && { label: "Callsign", value: vessel.callsign },
    vessel.type_name && { label: "Type",   value: vessel.type_name },
    vessel.length   && { label: "Length",   value: `${vessel.length} m` },
    vessel.width    && { label: "Width",    value: `${vessel.width} m` },
    (vessel.draft ?? vessel.draught) != null && {
      label: "Draught",
      value: `${vessel.draft ?? vessel.draught} m`,
    },
  ].filter(Boolean);

  const equasisFields = enrichment ? [
    enrichment.equasis_flag      && { label: "Flag",       value: enrichment.equasis_flag },
    enrichment.equasis_year_built && { label: "Built",     value: String(enrichment.equasis_year_built) },
    enrichment.equasis_gt        && { label: "GT",         value: enrichment.equasis_gt.toLocaleString() },
    enrichment.equasis_dwt       && { label: "DWT",        value: enrichment.equasis_dwt.toLocaleString() },
    enrichment.equasis_owner     && { label: "Owner",      value: enrichment.equasis_owner },
    enrichment.equasis_manager   && enrichment.equasis_manager !== "UNKNOWN" && { label: "Manager", value: enrichment.equasis_manager },
  ].filter(Boolean) : [];

  return (
    <div className="rounded-lg border border-green-400 bg-green-50 shadow-sm overflow-hidden">
      {/* Header row */}
      <div className="flex items-center justify-between px-4 py-3 bg-gray-50 border-b border-gray-100">
        <div className="font-semibold text-gray-900 text-base tracking-wide">
          {vessel.name || "Unnamed vessel"}
        </div>
        <div className="flex items-center gap-2">
          {enriching && (
            <span className="text-xs text-gray-400 italic">Fetching Equasis…</span>
          )}
          {particulars.length > 0 && (
            <button
              type="button"
              onClick={() => setExpanded(e => !e)}
              className="text-xs px-2 py-1 border border-gray-300 rounded hover:bg-gray-100 text-gray-600"
            >
              {expanded ? "Hide details ▲" : "Show details ▼"}
            </button>
          )}
          <button
            type="button"
            onClick={onClear}
            className="text-xs px-2 py-1 border border-red-200 rounded hover:bg-red-50 text-red-500"
          >
            ✕ Change
          </button>
        </div>
      </div>

      {/* Equasis summary — always visible when enriched */}
      {equasisFields.length > 0 && (
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-4 gap-y-2 px-4 py-3 bg-green-50 border-b border-green-100">
          {equasisFields.map(({ label, value }) => (
            <div key={label}>
              <div className="text-xs text-green-700 uppercase tracking-wide">{label}</div>
              <div className="text-sm font-medium text-gray-800">{value}</div>
            </div>
          ))}
        </div>
      )}

      {/* Expandable AIS particulars */}
      {expanded && particulars.length > 0 && (
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-4 gap-y-2 px-4 py-3">
          {particulars.map(({ label, value }) => (
            <div key={label}>
              <div className="text-xs text-gray-400 uppercase tracking-wide">{label}</div>
              <div className="text-sm font-medium text-gray-800">{value}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── VesselSearch ──────────────────────────────────────────────────────────────
// Searches AIS Harvester + Oskar DB in parallel. On select, fires onSelect(vessel)
// where vessel is either an Oskar DB vessel (has .id) or an AIS result (no .id).
function VesselSearch({ oskarVessels, selectedId, onSelect, onCreateNew, token, disabled, enrichment, enriching }) {
  const [query, setQuery] = useState("");
  const [aisResults, setAisResults] = useState([]);
  const [oskarResults, setOskarResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [open, setOpen] = useState(false);
  const timerRef = React.useRef(null);

  // Selected vessel display name
  const selectedVessel = oskarVessels.find(v => v.id === selectedId);
  const selectedLabel = selectedVessel
    ? `${selectedVessel.name}${selectedVessel.type_name ? " · " + selectedVessel.type_name : ""}`
    : null;

  function handleQueryChange(e) {
    const val = e.target.value;
    setQuery(val);
    if (val.length < 3) {
      setAisResults([]);
      setOskarResults([]);
      setOpen(false);
      return;
    }
    clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => doSearch(val), 350);
  }

  async function doSearch(q) {
    setSearching(true);
    try {
      const [aisRes, oskarRes] = await Promise.allSettled([
        apiFetch(`${AIS_API}/vessels/search?q=${encodeURIComponent(q)}&limit=10`).then(r => r.json()),
        apiFetch(`${process.env.REACT_APP_API_URL}/vessels?q=${encodeURIComponent(q)}`, {
          headers: { Authorization: `Bearer ${token}` }
        }).then(r => r.json()),
      ]);
      const ais = aisRes.status === "fulfilled" && Array.isArray(aisRes.value) ? aisRes.value : [];
      const oskar = oskarRes.status === "fulfilled" && Array.isArray(oskarRes.value) ? oskarRes.value : [];
      setAisResults(ais);
      setOskarResults(oskar);
      setOpen(true);
    } catch {
      setAisResults([]);
      setOskarResults([]);
    } finally {
      setSearching(false);
    }
  }

  const hasResults = aisResults.length > 0 || oskarResults.length > 0;

  return (
    <div className="space-y-3">
      {/* Selected vessel display — shown when a vessel is selected */}
      <div className={`border rounded-md p-3 min-h-[48px] ${selectedVessel ? "border-green-400 bg-green-50" : "border-dashed border-gray-300"}`}>
        <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Selected vessel</div>
        {!selectedVessel ? (
          <div className="text-sm text-gray-400">No vessel selected yet.</div>
        ) : (
          <VesselCard
            vessel={selectedVessel}
            enrichment={enrichment}
            enriching={enriching}
            onClear={() => { onSelect(null); setQuery(""); }}
          />
        )}
      </div>

      {/* Search input — hidden when vessel is selected */}
      {!selectedVessel && (
        <>
          <div className="relative">
            <input
              type="text"
              value={query}
              onChange={handleQueryChange}
              disabled={disabled}
              placeholder="Search by name, MMSI or IMO…"
              className="w-full border border-gray-300 rounded-md shadow-sm p-2 text-sm pr-8"
            />
            {searching && (
              <span className="absolute right-2 top-2.5 text-xs text-gray-400">…</span>
            )}
          </div>

          {/* Results dropdown */}
          {open && (
            <div className="border border-gray-200 rounded-md bg-white shadow-sm divide-y text-sm max-h-64 overflow-y-auto">
              {!hasResults && (
                <div className="px-3 py-2 text-gray-400 text-xs">No results found.</div>
              )}
              {oskarResults.length > 0 && (
                <>
                  <div className="px-3 py-1 text-xs font-semibold text-gray-400 bg-gray-50">In Oskar</div>
                  {oskarResults.map(v => (
                    <button
                      key={`oskar-${v.id}`}
                      type="button"
                      onClick={() => { onSelect({ source: "oskar", vessel: v }); setOpen(false); setQuery(""); }}
                      className="w-full text-left px-3 py-2 hover:bg-blue-50 flex justify-between items-center"
                    >
                      <span>{v.name}{v.type_name ? ` · ${v.type_name}` : ""}</span>
                      <span className="text-xs text-gray-400">{v.mmsi || v.imo || ""}</span>
                    </button>
                  ))}
                </>
              )}
              {aisResults.length > 0 && (
                <>
                  <div className="px-3 py-1 text-xs font-semibold text-gray-400 bg-gray-50">From AIS database</div>
                  {aisResults.map(v => (
                    <button
                      key={`ais-${v.mmsi}`}
                      type="button"
                      onClick={() => { onSelect({ source: "ais", vessel: v }); setOpen(false); setQuery(""); }}
                      className="w-full text-left px-3 py-2 hover:bg-blue-50 flex justify-between items-center"
                    >
                      <span>{v.name}{v.type_name ? ` · ${v.type_name}` : ""}</span>
                      <span className="text-xs text-gray-400">{[v.mmsi, v.length ? `${v.length}m` : null].filter(Boolean).join(" · ")}</span>
                    </button>
                  ))}
                </>
              )}
            </div>
          )}

          {/* Fallback: choose from existing dropdown */}
          <details className="text-xs text-gray-400">
            <summary className="cursor-pointer hover:text-gray-600">Or pick from list</summary>
            <select
              value={selectedId || ""}
              onChange={(e) => onSelect(e.target.value ? { source: "oskar", vessel: oskarVessels.find(v => v.id === Number(e.target.value)) } : null)}
              disabled={disabled}
              className="mt-1 w-full border border-gray-300 rounded-md p-2 text-sm"
            >
              <option value="">— Select vessel —</option>
              {oskarVessels.map((v) => (
                <option key={v.id} value={v.id}>{v.name}{v.type_name ? ` · ${v.type_name}` : ""}</option>
              ))}
            </select>
          </details>

          {!disabled && (
            <button type="button" onClick={onCreateNew}
              className="text-xs text-gray-500 hover:text-gray-700 hover:underline">
              + Create new vessel instead
            </button>
          )}
        </>
      )}
    </div>
  );
}

// ── Main ──────────────────────────────────────────────────────────────────────
export default function InspectionSetupPage() {
  const { inspectionId } = useParams();
  const { token, user, activeCompany } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const newTemplateParam = new URLSearchParams(location.search).get("newTemplate");

  // Core data
  const [inspection, setInspection] = useState(null);
  const [loadingData, setLoadingData] = useState(true);
  const [err, setErr] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [starting, setStarting] = useState(false);

  // Reference lists
  const [vessels, setVessels] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [companyMembers, setCompanyMembers] = useState([]);

  // Location / detail fields
  const [locationType, setLocationType] = useState("vessel");
  const [locationVesselId, setLocationVesselId] = useState(null);
  const [locationCompanyId, setLocationCompanyId] = useState(null);
  const [locationFreeText, setLocationFreeText] = useState("");
  const [summaryText, setSummaryText] = useState("");
  const [inspectorCompanyId, setInspectorCompanyId] = useState(null); // set to activeCompany after load
  const [inspectionDate, setInspectionDate] = useState("");
  const [inspectionTime, setInspectionTime] = useState("");
  const [inspectionReason, setInspectionReason] = useState("");
  const [inspectionReasonOther, setInspectionReasonOther] = useState("");

  // Inspectors
  const [inspectors, setInspectors] = useState([]);
  const [inspectorSearch, setInspectorSearch] = useState("");
  const [inspectorSearchResults, setInspectorSearchResults] = useState([]);
  const [selectedMember, setSelectedMember] = useState("");
  const [externalCompanyId, setExternalCompanyId] = useState(null);
  const inspectorDebounce = useRef(null);

  // Templates
  const [selectedTemplates, setSelectedTemplates] = useState([]);
  const [availableTemplates, setAvailableTemplates] = useState([]);
  const [templateDropdownValue, setTemplateDropdownValue] = useState("");

  // Vessel owner linking
  const [vesselOwnerCompanyId, setVesselOwnerCompanyId] = useState(null);

  // Attendees
  const [attendees, setAttendees] = useState([]);
  const [attendeeForm, setAttendeeForm] = useState({ name: "", email: "", role: "", company_id: "" });
  const [addingAttendee, setAddingAttendee] = useState(false);

  // Equasis enrichment
  const [enrichment, setEnrichment] = useState(null);
  const [enriching, setEnriching] = useState(false);

  // ── Fetch helpers ─────────────────────────────────────────────────────────
  const fetchInspectors = useCallback(async () => {
    try {
      const res = await api(`/india/inspections/${inspectionId}/inspectors`, { token });
      setInspectors(Array.isArray(res) ? res : []);
    } catch { setInspectors([]); }
  }, [inspectionId, token]);

  const fetchAttendees = useCallback(async () => {
    try {
      const res = await api(`/india/inspections/${inspectionId}/attendees`, { token });
      setAttendees(Array.isArray(res) ? res : []);
    } catch { setAttendees([]); }
  }, [inspectionId, token]);

  // ── On mount ──────────────────────────────────────────────────────────────
  useEffect(() => {
    if (!token || !user?.id || !inspectionId) return;
    setLoadingData(true);
    setErr(null);

    const fetches = [
      api(`/india/inspections/${inspectionId}`, { token }),
      api(`/vessels`, { token }),
      api("/companies/all", { token }),
      api(`/india/inspections/${inspectionId}/templates`, { token }),
      api("/india/templates", { token }),
    ];

    if (activeCompany?.id) {
      fetches.push(api(`/india/companies/${activeCompany.id}/members`, { token }));
    }

    Promise.all(fetches)
      .then(([inspRes, vesselsRes, companiesRes, selectedTplRes, availableTplRes, membersRes]) => {
        const insp = inspRes?.inspection || inspRes || null;
        setInspection(insp);
        if (insp && insp.status !== "draft") {
          navigate(`/inspections/${inspectionId}/questions`, { replace: true });
          return;
        }
        setVessels(Array.isArray(vesselsRes) ? vesselsRes : []);
        setCompanies(
          Array.isArray(companiesRes?.companies) ? companiesRes.companies :
          Array.isArray(companiesRes) ? companiesRes : []
        );
        setSelectedTemplates(Array.isArray(selectedTplRes) ? selectedTplRes : []);
        const allTpls = Array.isArray(availableTplRes) ? availableTplRes : [];
        setAvailableTemplates(allTpls);
        if (newTemplateParam) {
          const tplId = Number(newTemplateParam);
          const found = allTpls.find(t => t.id === tplId);
          if (found) {
            setSelectedTemplates(prev =>
              prev.find(t => t.template_id === tplId) ? prev : [...prev, { template_id: found.id, name: found.name, version: found.version }]
            );
          }
        }
        setCompanyMembers(Array.isArray(membersRes) ? membersRes : []);

        if (insp) {
          setLocationType(insp.location_type || "vessel");
          setLocationVesselId(insp.location_vessel_id || null);
          setLocationCompanyId(insp.location_company_id || null);
          setLocationFreeText(insp.location_free_text || "");
          setSummaryText(insp.summary_text || "");
          setInspectorCompanyId(insp.inspector_company_id || activeCompany?.id || null);
          const savedReason = insp.inspection_reason || "";
          const allKnownReasons = [...REASON_OPTIONS_VESSEL, ...REASON_OPTIONS_COMPANY];
          if (savedReason && !allKnownReasons.includes(savedReason)) {
            setInspectionReason("Other");
            setInspectionReasonOther(savedReason);
          } else {
            setInspectionReason(savedReason);
          }
          if (insp.inspection_date) setInspectionDate(insp.inspection_date.slice(0, 10));
          if (insp.inspection_time) setInspectionTime(insp.inspection_time.slice(0, 5));
        }
        const today = new Date().toISOString().slice(0, 10);
        setInspectionDate(prev => prev || today);
      })
      .catch((e) => setErr(e?.message || "Failed to load"))
      .finally(() => setLoadingData(false));

    fetchInspectors();
    fetchAttendees();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, user?.id, inspectionId, activeCompany?.id]);

  // ── Handle vessel selection from VesselSearch ─────────────────────────────
  async function handleVesselSelect(result) {
    setEnrichment(null);
    if (!result) {
      setLocationVesselId(null);
      return;
    }
    if (result.source === "oskar") {
      const v = result.vessel;
      // Ensure vessel is in the local list so VesselCard can find it
      setVessels(prev => prev.find(x => x.id === v.id) ? prev : [...prev, v]);
      setLocationVesselId(v.id);
      // If vessel already has stored Equasis fields, show them immediately
      if (v.equasis_owner || v.equasis_manager || v.equasis_flag || v.equasis_gt) {
        setEnrichment({
          equasis_owner: v.equasis_owner,
          equasis_owner_imo: v.equasis_owner_imo,
          equasis_manager: v.equasis_manager,
          equasis_flag: v.equasis_flag,
          equasis_flag_code: v.equasis_flag_code,
          equasis_gt: v.equasis_gt,
          equasis_dwt: v.equasis_dwt,
          equasis_year_built: v.equasis_year_built,
        });
      }
      // If vessel has IMO, trigger enrichment to refresh data
      if (v.imo) triggerEnrichment(String(v.imo), v.id);
      return;
    }
    // AIS result — check if vessel already exists in Oskar by MMSI
    const v = result.vessel;
    const existing = v.mmsi ? vessels.find(x => String(x.mmsi) === String(v.mmsi)) : null;
    if (existing) {
      setVessels(prev => prev.find(x => x.id === existing.id) ? prev : [...prev, existing]);
      setLocationVesselId(existing.id);
      if (existing.equasis_owner || existing.equasis_manager || existing.equasis_flag || existing.equasis_gt) {
        setEnrichment({
          equasis_owner: existing.equasis_owner,
          equasis_owner_imo: existing.equasis_owner_imo,
          equasis_manager: existing.equasis_manager,
          equasis_flag: existing.equasis_flag,
          equasis_flag_code: existing.equasis_flag_code,
          equasis_gt: existing.equasis_gt,
          equasis_dwt: existing.equasis_dwt,
          equasis_year_built: existing.equasis_year_built,
        });
      }
      if (v.imo) triggerEnrichment(String(v.imo), existing.id);
      return;
    }
    // Not in Oskar — silently create vessel in Oskar DB, then select it
    try {
      const created = await api("/vessels", {
        token,
        method: "POST",
        body: {
          name: v.name || null,
          mmsi: v.mmsi ? String(v.mmsi) : null,
          imo: v.imo ? String(v.imo) : null,
          callsign: v.callsign || null,
          length: v.length || null,
          width: v.width || null,
          draft: v.draught || null,
        },
      });
      const newId = created?.id ?? created?.vessel?.id ?? null;
      const newVessel = created?.vessel ?? null;
      if (newId && newVessel?.name) {
        setVessels(prev => [...prev, newVessel]);
        setLocationVesselId(newId);
      } else if (newId) {
        try {
          const fetched = await apiFetch(
            `${process.env.REACT_APP_API_URL}/vessels/${newId}?user_id=${user.id}`,
            { headers: { Authorization: `Bearer ${token}` } }
          ).then(r => r.json());
          setVessels(prev => [...prev, fetched]);
        } catch {
          setVessels(prev => [...prev, { id: newId, name: "New vessel" }]);
        }
        setLocationVesselId(newId);
      }
      // Immediately persist the vessel link to the inspection — don't wait for user to hit Save
      if (newId) {
        try {
          await saveSetup({ locationVesselId: newId });
        } catch {
          // non-fatal — user can still save manually
        }
      }
      // If AIS vessel has IMO, enrich in background — pass newId so the PATCH back to Oskar works
      if (v.imo) triggerEnrichment(String(v.imo), newId);
    } catch (e) {
      // If vessel already exists (409), fetch it by MMSI and select it
      if (e?.status === 409 || (e?.message && e.message.includes("already exists"))) {
        try {
          const found = await api(`/vessels?q=${v.mmsi}`, { token });
          const existing = Array.isArray(found) ? found[0] : null;
          if (existing) {
            setVessels(prev => prev.find(x => x.id === existing.id) ? prev : [...prev, existing]);
            setLocationVesselId(existing.id);
            if (existing.equasis_owner || existing.equasis_manager || existing.equasis_flag || existing.equasis_gt) {
              setEnrichment({
                equasis_owner: existing.equasis_owner,
                equasis_owner_imo: existing.equasis_owner_imo,
                equasis_manager: existing.equasis_manager,
                equasis_flag: existing.equasis_flag,
                equasis_flag_code: existing.equasis_flag_code,
                equasis_gt: existing.equasis_gt,
                equasis_dwt: existing.equasis_dwt,
                equasis_year_built: existing.equasis_year_built,
              });
            }
            if (v.imo) triggerEnrichment(String(v.imo), existing.id);
          }
        } catch {
          console.error("Failed to recover vessel after 409", e);
        }
      } else {
        console.error("Failed to create vessel from AIS result", e);
      }
    }
  }

  async function triggerEnrichment(imo, vesselId = null) {
    setEnriching(true);
    try {
      const res = await apiFetch(`${AIS_API}/vessels/${imo}/enrich`, { method: "POST" });
      if (res.ok) {
        const json = await res.json();
        const data = json.data || null;
        setEnrichment(data);
        // Persist key Equasis fields back to Oskar vessels table
        if (data) {
          const resolvedId = vesselId ?? vessels.find(v => String(v.imo) === String(imo))?.id ?? null;
          if (resolvedId) {
            try {
              await api(`/vessels/${resolvedId}`, {
                    token,
                    method: "PATCH",
                    body: {
                      equasis_manager: data.equasis_manager && data.equasis_manager !== "UNKNOWN"
                        ? data.equasis_manager : null,
                      equasis_manager_imo: data.equasis_manager_imo || null,
                      equasis_manager_address: data.equasis_manager_address || null,
                      equasis_owner: data.equasis_owner && data.equasis_owner !== "UNKNOWN"
                        ? data.equasis_owner : null,
                      equasis_flag: data.equasis_flag || null,
                      equasis_year_built: data.equasis_year_built || null,
                      equasis_gt: data.equasis_gt || null,
                      equasis_dwt: data.equasis_dwt || null,
                    },
                  });
                  // Auto-create or link company from Equasis manager data
                  if (data.equasis_manager && data.equasis_manager !== "UNKNOWN") {
                    try {
                      await api(`/vessels/${resolvedId}/link-company`, {
                        token,
                        method: "POST",
                        body: {
                          equasis_manager: data.equasis_manager,
                          equasis_manager_imo: data.equasis_manager_imo || null,
                          equasis_manager_address: data.equasis_manager_address || null,
                          equasis_owner: data.equasis_owner && data.equasis_owner !== "UNKNOWN"
                            ? data.equasis_owner : null,
                        },
                      });
                    } catch {
                      // silent — non-fatal
                    }
                  }
            } catch {
              // silent — non-fatal
            }
          }
        }
      }
    } catch {
      // silent — enrichment is best-effort
    } finally {
      setEnriching(false);
    }
  }

  // ── Auto-fill attendee company from selected vessel ───────────────────────
  useEffect(() => {
    setVesselOwnerCompanyId(null);
    if (!locationVesselId) return;
    const vessel = vessels.find(v => v.id === locationVesselId);
    if (vessel?.company_id) {
      setAttendeeForm(f => ({ ...f, company_id: String(vessel.company_id) }));
    }
  }, [locationVesselId, vessels]);

  // ── Auto-fill attendee company from location company ─────────────────────
  useEffect(() => {
    if (!locationCompanyId || locationType !== "company") return;
    setAttendeeForm(f => ({ ...f, company_id: String(locationCompanyId) }));
  }, [locationCompanyId, locationType]);

  // ── Return from AddCompany / AddVessel ────────────────────────────────────
  useEffect(() => {
    if (!location.state?.newCompany) return;
    const nc = location.state.newCompany;
    setCompanies((prev) =>
      prev.find((c) => c.id === nc.id) ? prev : [...prev, nc].sort((a, b) => a.name.localeCompare(b.name))
    );
    if (location.state.companyFor === "vessel_owner") {
      setAttendeeForm(f => ({ ...f, company_id: String(nc.id) }));
      setVesselOwnerCompanyId(nc.id);
    }
    if (location.state.companyFor === "location") {
      setAttendeeForm(f => ({ ...f, company_id: String(nc.id) }));
      setTimeout(() => setLocationCompanyId(nc.id), 50);
    }
    if (location.state.companyFor === "vessel_owner" && location.state.vesselId) {
      const vesselId = location.state.vesselId;
      api(`/vessels/${vesselId}`, { token, method: "PATCH", body: { company_id: nc.id } })
        .then(() => api(`/vessels`, { token }))
        .then((res) => {
          setVessels(Array.isArray(res) ? res : []);
          setLocationVesselId(vesselId);
        })
        .catch(() => {});
    }
    window.history.replaceState({}, "");
  }, [location.state]);

  useEffect(() => {
    if (!location.state?.newVessel) return;
    const nv = location.state.newVessel;
    setVessels((prev) =>
      prev.find((v) => v.id === nv.id) ? prev : [...prev, nv].sort((a, b) => a.name.localeCompare(b.name))
    );
    if (nv.id) setLocationVesselId(nv.id);
    window.history.replaceState({}, "");
  }, [location.state]);

  // ── Inspector search (debounced) ──────────────────────────────────────────
  useEffect(() => {
    clearTimeout(inspectorDebounce.current);
    if (inspectorSearch.length < 2) { setInspectorSearchResults([]); return; }
    inspectorDebounce.current = setTimeout(async () => {
      try {
        const res = await api(`/india/users/search?q=${encodeURIComponent(inspectorSearch)}`, { token });
        setInspectorSearchResults(Array.isArray(res) ? res : []);
      } catch { setInspectorSearchResults([]); }
    }, 300);
    return () => clearTimeout(inspectorDebounce.current);
  }, [inspectorSearch, token]);

  // ── Inspector actions ─────────────────────────────────────────────────────
  async function addInspector(userId, companyId) {
    try {
      await api(`/india/inspections/${inspectionId}/inspectors`, {
        token, method: "POST",
        body: { user_id: userId, company_id: companyId ?? null },
      });
      await fetchInspectors();
    } catch (e) { setErr(e?.message || "Failed to add inspector"); }
  }

  async function removeInspector(userId) {
    try {
      await api(`/india/inspections/${inspectionId}/inspectors/${userId}`, { token, method: "DELETE" });
      await fetchInspectors();
    } catch (e) { setErr(e?.message || "Failed to remove inspector"); }
  }

  async function handleAddFromCompany(e) {
    const val = e.target.value;
    if (!val) return;
    setSelectedMember("");
    const member = companyMembers.find((m) => String(m.id) === val);
    if (member) await addInspector(member.id, activeCompany?.id);
  }

  // ── Vessel owner linking ──────────────────────────────────────────────────
  async function handleLinkVesselOwner() {
    if (!vesselOwnerCompanyId || !locationVesselId) return;
    try {
      await api(`/vessels/${locationVesselId}`, { token, method: "PATCH", body: { company_id: vesselOwnerCompanyId } });
      const res = await api(`/vessels`, { token });
      setVessels(Array.isArray(res) ? res : []);
      setAttendeeForm(f => ({ ...f, company_id: String(vesselOwnerCompanyId) }));
      setVesselOwnerCompanyId(null);
    } catch (e) {
      setErr(e?.message || "Failed to link company");
    }
  }

  // ── Attendee actions ──────────────────────────────────────────────────────
  async function handleAddAttendee() {
    if (!attendeeForm.name.trim() || !attendeeForm.email.trim()) return;
    setAddingAttendee(true);
    try {
      await api(`/india/inspections/${inspectionId}/attendees`, {
        token, method: "POST",
        body: {
          name: attendeeForm.name.trim(),
          email: attendeeForm.email.trim() || null,
          role_in_company: attendeeForm.role.trim() || null,
          company_id: attendeeForm.company_id ? Number(attendeeForm.company_id) : null,
        },
      });
      await fetchAttendees();
      setAttendeeForm({ name: "", email: "", role: "", company_id: "" });
    } catch (e) { setErr(e?.message || "Failed to add attendee"); }
    finally { setAddingAttendee(false); }
  }

  async function removeAttendee(attendeeId) {
    try {
      await api(`/india/inspections/${inspectionId}/attendees/${attendeeId}`, { token, method: "DELETE" });
      await fetchAttendees();
    } catch (e) { setErr(e?.message || "Failed to remove attendee"); }
  }

  // ── Save / start ──────────────────────────────────────────────────────────
  async function saveSetup(overrides = {}) {
    const effectiveVesselId = overrides.locationVesselId !== undefined
      ? overrides.locationVesselId
      : locationVesselId;
    const vesselName = vessels.find(v => v.id === effectiveVesselId)?.name || null;
    const templateName = selectedTemplates[0]?.name || null;
    const effectiveReason = inspectionReason === "Other" ? inspectionReasonOther : inspectionReason;
    const datePart = inspectionDate ? new Date(inspectionDate).toLocaleDateString("en-GB", { month: "short", year: "numeric" }) : null;
    const nameParts = [vesselName, templateName, effectiveReason, datePart].filter(Boolean);
    const autoName = nameParts.length > 0 ? nameParts.join(" — ") : null;

    await api(`/india/inspections/${inspectionId}/setup`, {
      token,
      method: "PATCH",
      body: {
        name: autoName,
        location_type: locationType,
        location_vessel_id: locationType === "vessel" ? effectiveVesselId : null,
        location_company_id: locationType === "company" ? locationCompanyId : null,
        location_free_text: locationFreeText.trim() || null,
        inspection_reason: (inspectionReason === "Other" ? inspectionReasonOther : inspectionReason).trim() || null,
        inspection_date: inspectionDate || null,
        inspection_time: inspectionTime || null,
        template_ids: selectedTemplates.map(t => t.template_id),
        summary_text: summaryText.trim() || null,
        inspector_company_id: inspectorCompanyId || null,
      },
    });
  }

  async function handleSave(e) {
    e.preventDefault();
    setErr(null);
    setSaving(true);
    setSaved(false);
    try {
      await saveSetup();
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (e) {
      setErr(e?.message || "Failed to save");
    } finally {
      setSaving(false);
    }
  }

  async function handleStart() {
    setErr(null);
    setStarting(true);
    try {
      await saveSetup();
      await api(`/india/inspections/${inspectionId}/start`, { token, method: "POST" });
      navigate(`/inspections/${inspectionId}/questions`);
    } catch (e) {
      setErr(e?.message || "Failed to start inspection");
      setStarting(false);
    }
  }

  // ── Render ────────────────────────────────────────────────────────────────
  const REASON_OPTIONS = locationType === "vessel" ? REASON_OPTIONS_VESSEL : REASON_OPTIONS_COMPANY;

  const returnPath = `/inspections/${inspectionId}/setup`;

  if (loadingData) {
    return (
      <div className="pt-8 sm:pt-16 min-h-screen bg-petrol-light px-3 sm:px-6 flex items-center justify-center">
        <div className="text-sm text-gray-500">Loading…</div>
      </div>
    );
  }

  const selectedVessel = vessels.find(v => v.id === locationVesselId) || null;

  const locationLabel =
    locationType === "vessel" ? "Vessel" :
    locationType === "company" ? "Company" :
    locationType === "site" ? "Site" : "Location";

  return (
    <div className="pt-4 sm:pt-16 min-h-screen bg-petrol-light px-3 sm:px-6 pb-16">
      <div className="max-w-2xl mx-auto">

        {/* Header */}
        <div className="mb-6">
          <button type="button" onClick={() => navigate("/inspections")}
            className="text-sm text-gray-500 hover:text-gray-700 mb-3 flex items-center gap-1">
            ← Back to inspections
          </button>
          <h1 className="text-2xl font-bold text-gray-900">
            {inspection?.name || `Inspection #${inspectionId}`}
          </h1>
          <p className="text-sm text-gray-500 mt-1">Complete the setup, then start the inspection.</p>
        </div>

        {err && (
          <div className="mb-4 text-sm text-red-700 border border-red-200 bg-white rounded p-3">{err}</div>
        )}

        <form onSubmit={handleSave} className="space-y-6">

          {/* Section 1 — Type */}
          <div className="bg-white rounded-lg shadow p-6 space-y-3">
            <div className="flex items-center gap-2 text-xs font-semibold text-gray-500 uppercase tracking-wide">
              <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-[#1e3a5f] text-white text-xs font-bold shrink-0">1</span>
              Inspection type
            </div>
            <select
              value={locationType}
              onChange={(e) => { setLocationType(e.target.value); setInspectionReason(""); setInspectionReasonOther(""); }}
              className="w-full border border-gray-300 rounded-md shadow-sm p-2 text-sm"
            >
              <option value="vessel">On board a vessel</option>
              <option value="company">At a company / office</option>
              <option value="site">On site</option>
              <option value="other">Other</option>
            </select>
            {locationType === "other" && (
              <div className="mt-3">
                <label className="block text-sm font-medium text-gray-700 mb-1">Inspection name / description</label>
                <input
                  type="text"
                  value={locationFreeText}
                  onChange={(e) => setLocationFreeText(e.target.value)}
                  placeholder="e.g. Office audit, yard visit, document review…"
                  className="w-full border border-gray-300 rounded-md shadow-sm p-2 text-sm"
                />
              </div>
            )}
          </div>

          {/* Section 2 — Templates */}
          <div className={`rounded-lg shadow p-6 space-y-3 ${selectedTemplates.length === 0 ? "bg-amber-50 border border-amber-200" : "bg-white"}`}>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 text-xs font-semibold text-gray-500 uppercase tracking-wide">
                <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-[#1e3a5f] text-white text-xs font-bold shrink-0">2</span>
                Templates
              </div>
              {selectedTemplates.length === 0 && (
                <span className="text-xs text-amber-700 font-medium">Required to start</span>
              )}
            </div>

            {/* Selected templates box */}
            <div className={`border rounded-md p-3 min-h-[48px] ${selectedTemplates.length > 0 ? "border-green-400 bg-green-50" : "border-dashed border-gray-300"}`}>
              <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Selected templates</div>
              {selectedTemplates.length === 0 ? (
                <div className="text-sm text-gray-400">No templates selected yet.</div>
              ) : (
                <div className="flex flex-wrap gap-2">
                  {selectedTemplates.map((t) => (
                    <span key={t.template_id}
                      className="inline-flex items-center gap-1 bg-white border border-gray-200 text-gray-800 text-sm px-3 py-1 rounded-full shadow-sm">
                      {t.name}
                      {t.version && <span className="text-gray-400 text-xs ml-1">v{t.version}</span>}
                      <button
                        type="button"
                        onClick={() => setSelectedTemplates(prev => prev.filter(x => x.template_id !== t.template_id))}
                        className="ml-1 text-gray-400 hover:text-red-500 text-xs leading-none"
                      >✕</button>
                    </span>
                  ))}
                </div>
              )}
            </div>

            {/* Add template dropdown */}
            <div className="flex gap-2 w-full min-w-0">
              <select
                value={templateDropdownValue}
                onChange={(e) => setTemplateDropdownValue(e.target.value)}
                className="flex-1 min-w-0 border border-gray-300 rounded-md shadow-sm p-2 text-sm"
              >
                <option value="">— Select a template —</option>
                {availableTemplates
                  .filter(t => t.has_rules && !selectedTemplates.some(s => s.template_id === t.id))
                  .map(t => (
                    <option key={t.id} value={t.id}>
                      {t.name}
                    </option>
                  ))}
              </select>
              <button
                type="button"
                disabled={!templateDropdownValue}
                onClick={() => {
                  const tpl = availableTemplates.find(t => String(t.id) === String(templateDropdownValue));
                  if (!tpl) return;
                  setSelectedTemplates(prev => [...prev, { template_id: tpl.id, name: tpl.name, version: tpl.version }]);
                  setTemplateDropdownValue("");
                }}
                className={`px-3 py-2 text-sm rounded-md transition disabled:opacity-40 ${templateDropdownValue ? "bg-blue-600 text-white hover:bg-blue-700" : "border border-gray-300 hover:bg-gray-50"}`}
              >
                Select
              </button>
            </div>
            <button
              type="button"
              onClick={async () => { try { await saveSetup(); } catch {} navigate(`/templates/new?returnTo=${encodeURIComponent(returnPath)}`); }}
              className="text-xs text-blue-600 hover:underline mt-1"
            >
              ＋ Create new template
            </button>
          </div>

          {/* Section 3 — Assign Inspectors */}
          <div className="bg-white rounded-lg shadow p-6 space-y-4">
            <div className="flex items-center gap-2 text-xs font-semibold text-gray-500 uppercase tracking-wide">
              <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-[#1e3a5f] text-white text-xs font-bold shrink-0">3</span>
              Assign Inspectors
            </div>

            {/* Assigned inspectors box */}
            <div className={`border rounded-md p-3 min-h-[48px] ${inspectors.length > 0 ? "border-green-400 bg-green-50" : "border-dashed border-gray-300"}`}>
              <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Assigned inspectors</div>
              {inspectors.length === 0 && <div className="text-sm text-gray-400">No inspectors assigned yet.</div>}
              {inspectors.length > 0 && (
                <div className="flex flex-wrap gap-2">
                  {inspectors.map((ins) => (
                    <span key={ins.user_id ?? ins.id}
                      className="inline-flex items-center gap-1 bg-white border border-gray-200 text-gray-800 text-sm px-3 py-1 rounded-full shadow-sm">
                      {ins.first_name || ins.last_name
                        ? `${ins.first_name ?? ""} ${ins.last_name ?? ""}`.trim()
                        : ins.email ?? `User #${ins.user_id}`}
                      <button type="button" onClick={() => removeInspector(ins.user_id ?? ins.id)}
                        className="ml-1 text-gray-400 hover:text-red-500 text-xs leading-none">✕</button>
                    </span>
                  ))}
                </div>
              )}
            </div>

            {/* Search own company members */}
            {companyMembers.length > 0 && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Search inspectors from your company</label>
                <InspectorCompanySearch
                  members={companyMembers}
                  alreadyAdded={inspectors}
                  onSelect={(m) => addInspector(m.id, activeCompany?.id)}
                />
              </div>
            )}

            {/* Inspector from another company — collapsible, two-step */}
            <details className="text-sm">
              <summary className="cursor-pointer text-gray-500 hover:text-gray-700 select-none">
                Inspector from another company
              </summary>
              <div className="mt-2 space-y-3">

                {/* Step 1 — choose company */}
                <div>
                  <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">1. Choose company</label>
                  <CompanySearch
                    companies={companies.filter(c => c.id !== activeCompany?.id)}
                    value={externalCompanyId}
                    onSelect={(id) => { setExternalCompanyId(id); setInspectorSearch(""); setInspectorSearchResults([]); }}
                  />
                  <button
                    type="button"
                    onClick={() => navigate(`/add-company?returnTo=${encodeURIComponent(returnPath)}`)}
                    className="mt-1 text-xs text-gray-400 hover:text-gray-600 hover:underline"
                  >
                    + Company not in list? Add it
                  </button>
                </div>

                {/* Step 2 — search inspector within chosen company (only shown after company selected) */}
                {externalCompanyId && (
                  <div>
                    <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">2. Search inspector</label>
                    <input
                      type="text"
                      value={inspectorSearch}
                      onChange={(e) => setInspectorSearch(e.target.value)}
                      placeholder="Search by name or email…"
                      className="w-full border border-gray-300 rounded-md shadow-sm p-2 text-sm"
                    />
                    {inspectorSearchResults.length > 0 && (
                      <div className="mt-1 border rounded-md divide-y bg-white shadow-sm">
                        {inspectorSearchResults
                          .filter(r => r.company_id === externalCompanyId)
                          .map((r) => (
                            <button
                              key={`${r.id}-${r.company_id}`}
                              type="button"
                              onClick={() => { addInspector(r.id, r.company_id); setInspectorSearch(""); setInspectorSearchResults([]); }}
                              className="w-full text-left px-3 py-2 hover:bg-gray-50 text-sm"
                            >
                              <span className="font-medium">
                                {r.first_name || r.last_name ? `${r.first_name ?? ""} ${r.last_name ?? ""}`.trim() : r.email}
                              </span>
                              {r.email && <span className="block text-xs text-gray-400">{r.email}</span>}
                            </button>
                          ))}
                      </div>
                    )}
                  </div>
                )}

              </div>
            </details>

            {/* Not in list — invite */}
            <InspectorInviteForm
              inspectionId={inspectionId}
              token={token}
              activeCompany={activeCompany}
              onAdded={fetchInspectors}
            />
          </div>

          {/* On behalf of */}
          <div className="bg-white rounded-lg shadow p-6 space-y-3">
            <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide">On behalf of</div>
            <p className="text-xs text-gray-500">
              The company you are carrying out this inspection for (default is your company).
            </p>

            {/* Selected company box */}
            <div className={`border rounded-md p-3 min-h-[48px] ${inspectorCompanyId ? "border-green-400 bg-green-50" : "border-dashed border-gray-300"}`}>
              <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Selected company</div>
              {!inspectorCompanyId ? (
                <div className="text-sm text-gray-400">No company selected yet.</div>
              ) : (
                <div className="flex flex-wrap gap-2">
                  <span className="inline-flex items-center gap-1 bg-white border border-gray-200 text-gray-800 text-sm px-3 py-1 rounded-full shadow-sm">
                    {companies.find(c => c.id === inspectorCompanyId)?.name || `Company #${inspectorCompanyId}`}
                    <button type="button" onClick={() => setInspectorCompanyId(null)}
                      className="ml-1 text-gray-400 hover:text-red-500 text-xs leading-none">✕</button>
                  </span>
                </div>
              )}
            </div>

            {/* Dropdown to select */}
            <OnBehalfDropdown
              companies={companies}
              onSelect={(id) => setInspectorCompanyId(id)}
            />
            <button
              type="button"
              onClick={async () => { try { await saveSetup(); } catch {} navigate(`/add-company?returnTo=${encodeURIComponent(returnPath)}`); }}
              className="text-xs text-blue-600 hover:underline mt-1"
            >
              + Add new company
            </button>
          </div>

          {/* Section 4 — Location */}
          <div className="bg-white rounded-lg shadow p-6 space-y-4">
            <div className="flex items-center gap-2 text-xs font-semibold text-gray-500 uppercase tracking-wide">
              <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-[#1e3a5f] text-white text-xs font-bold shrink-0">4</span>
              {locationLabel}
            </div>

            {locationType === "vessel" && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Vessel</label>
                <VesselSearch
                  oskarVessels={vessels}
                  selectedId={locationVesselId}
                  onSelect={handleVesselSelect}
                  onCreateNew={async () => { try { await saveSetup(); } catch {} navigate(`/vessels/add?returnTo=${encodeURIComponent(returnPath)}`, { state: { vesselFor: "location" } }); }}
                  token={token}
                  enrichment={enrichment}
                  enriching={enriching}
                />
                {selectedVessel && !selectedVessel.company_id && !enrichment?.equasis_owner && (
                  <div className="mt-2 rounded-md border border-amber-200 bg-amber-50 p-3 space-y-2">
                    <div className="text-sm text-amber-800 font-medium">This vessel has no owner on record.</div>

                    <CompanySearch
                      companies={companies}
                      value={vesselOwnerCompanyId}
                      onSelect={(id) => setVesselOwnerCompanyId(id)}
                    />
                    <div className="flex gap-2 items-center">
                      <button
                        type="button"
                        onClick={handleLinkVesselOwner}
                        disabled={!vesselOwnerCompanyId}
                        className="px-3 py-2 text-sm bg-amber-600 text-white rounded-md hover:bg-amber-700 disabled:opacity-40"
                      >
                        Link owner
                      </button>
                      <button
                        type="button"
                        onClick={async () => { try { await saveSetup(); } catch {} navigate(`/add-company?returnTo=${encodeURIComponent(returnPath)}`, { state: { companyFor: "vessel_owner", vesselId: locationVesselId } }); }}
                        className="text-xs text-amber-700 hover:underline"
                      >
                        + Add new company instead
                      </button>
                    </div>
                  </div>
                )}
                {selectedVessel?.company_id && (
                  <div className="mt-2 text-xs text-green-700">
                    Owner: {companies.find(c => c.id === selectedVessel.company_id)?.name || `Company #${selectedVessel.company_id}`}
                  </div>
                )}
              </div>
            )}

            {locationType === "company" && (
              <CompanyPicker label="Company / office" value={locationCompanyId}
                onChange={setLocationCompanyId} companies={companies}
                onAddNew={async () => { try { await saveSetup(); } catch {} navigate(`/add-company?returnTo=${encodeURIComponent(returnPath)}`, { state: { companyFor: "location" } }); }}
              />
            )}

            {(locationType === "site" || locationType === "other") && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {locationType === "site" ? "Site name" : "Location description"}
                </label>
                <input type="text" value={locationFreeText} onChange={(e) => setLocationFreeText(e.target.value)}
                  placeholder={locationType === "site" ? "e.g. Terminal A, Shipyard…" : "Describe the location…"}
                  className="w-full border border-gray-300 rounded-md shadow-sm p-2 text-sm" />
              </div>
            )}

            {locationType !== "site" && locationType !== "other" && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Location detail</label>
                <input type="text" value={locationFreeText} onChange={(e) => setLocationFreeText(e.target.value)}
                  placeholder={locationType === "vessel" ? "e.g. Port of Rotterdam, anchorage, dry dock…" : "e.g. Head office, yard, warehouse…"}
                  className="w-full border border-gray-300 rounded-md shadow-sm p-2 text-sm" />
              </div>
            )}

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Date</label>
                <input type="date" value={inspectionDate} onChange={(e) => setInspectionDate(e.target.value)}
                  className="w-full border border-gray-300 rounded-md shadow-sm p-2 text-sm" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Time</label>
                <input type="time" value={inspectionTime} onChange={(e) => setInspectionTime(e.target.value)}
                  className="w-full border border-gray-300 rounded-md shadow-sm p-2 text-sm" />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Reason for inspection</label>
              <select
                value={inspectionReason}
                onChange={(e) => setInspectionReason(e.target.value)}
                className="w-full border border-gray-300 rounded-md shadow-sm p-2 text-sm"
              >
                <option value="">— Select reason —</option>
                {REASON_OPTIONS.map((r) => (
                  <option key={r} value={r}>{r}</option>
                ))}
              </select>
              {inspectionReason === "Other" && (
                <input
                  type="text"
                  value={inspectionReasonOther}
                  onChange={(e) => setInspectionReasonOther(e.target.value)}
                  placeholder="Describe the reason…"
                  className="mt-2 w-full border border-gray-300 rounded-md shadow-sm p-2 text-sm"
                />
              )}
            </div>
          </div>

          {/* Section 5 �� Attendees */}
          <div className="bg-white rounded-lg shadow p-6 space-y-4">
            <h2 className="flex items-center gap-2 text-sm font-semibold text-gray-500 uppercase tracking-wide">
              <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-[#1e3a5f] text-white text-xs font-bold shrink-0">5</span>
              Attendees (from inspected party)
            </h2>

            {/* Added attendees box */}
            <div className={`rounded-md border p-3 ${attendees.length > 0 ? "border-green-400 bg-green-50" : "border-gray-200 bg-gray-50"}`}>
              <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Added attendees</div>
              {attendees.length === 0 ? (
                <div className="text-sm text-gray-400">No attendees added yet.</div>
              ) : (
                <div className="divide-y">
                  {attendees.map((a) => (
                    <div key={a.id} className="flex items-start justify-between py-2 first:pt-0 last:pb-0">
                      <div>
                        <div className="text-sm font-medium">{a.name}</div>
                        <div className="text-xs text-gray-500">
                          {[a.role_in_company, a.email, a.company_name].filter(Boolean).join(" · ")}
                        </div>
                      </div>
                      <button type="button" onClick={() => removeAttendee(a.id)}
                        className="text-gray-400 hover:text-red-500 text-xs mt-0.5 ml-3 shrink-0">✕</button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Add attendee — collapsible */}
            <details>
              <summary className="cursor-pointer text-sm text-gray-600 hover:text-gray-900 select-none font-medium">
                + Add attendee
              </summary>
              <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2">
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Name *</label>
                  <input type="text" value={attendeeForm.name}
                    onChange={(e) => setAttendeeForm((f) => ({ ...f, name: e.target.value }))}
                    placeholder="Full name"
                    className="w-full border border-gray-300 rounded-md shadow-sm p-2 text-sm" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Email *</label>
                  <input type="email" value={attendeeForm.email}
                    onChange={(e) => setAttendeeForm((f) => ({ ...f, email: e.target.value }))}
                    className="w-full border border-gray-300 rounded-md shadow-sm p-2 text-sm" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Role</label>
                  <input type="text" value={attendeeForm.role}
                    onChange={(e) => setAttendeeForm((f) => ({ ...f, role: e.target.value }))}
                    placeholder="e.g. Captain, Chief Engineer…"
                    className="w-full border border-gray-300 rounded-md shadow-sm p-2 text-sm" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Company</label>
                  <select value={attendeeForm.company_id}
                    onChange={(e) => setAttendeeForm((f) => ({ ...f, company_id: e.target.value }))}
                    className="w-full border border-gray-300 rounded-md shadow-sm p-2 text-sm">
                    <option value="">— Select —</option>
                    {companies.map((c) => (
                      <option key={c.id} value={c.id}>{c.name}</option>
                    ))}
                  </select>
                </div>
              </div>
              <button type="button" onClick={handleAddAttendee}
                disabled={addingAttendee || !attendeeForm.name.trim() || !attendeeForm.email.trim()}
                className={`mt-3 px-4 py-2 text-sm rounded-md disabled:opacity-50 transition ${attendeeForm.name.trim() && attendeeForm.email.trim() ? "bg-blue-600 text-white hover:bg-blue-700" : "border border-gray-300 hover:bg-gray-50"}`}>
                {addingAttendee ? "Adding…" : "Add attendee"}
              </button>
            </details>
          </div>

          {/* Section 6 — Actions */}
          <div className="bg-white rounded-lg shadow p-6 space-y-3">
            <div className="flex items-center gap-3">
              <button type="submit" disabled={saving}
                className="px-4 py-2 rounded-md border border-gray-300 bg-white text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-60">
                {saving ? "Saving…" : "Save"}
              </button>
              <button type="button" onClick={handleStart} disabled={starting || saving || selectedTemplates.length === 0}
                className="px-5 py-2 rounded-md bg-gray-900 text-white text-sm font-medium hover:bg-gray-700 disabled:opacity-60">
                {starting ? "Starting…" : "Start inspection →"}
              </button>
              {saved && <span className="text-sm text-green-700 font-medium">✓ Saved</span>}
            </div>
            {selectedTemplates.length === 0 && (
              <p className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-3 py-2">
                📋 Please select at least one template before starting the inspection.
              </p>
            )}
          </div>

        </form>
      </div>
    </div>
  );
}
